import moment from 'moment'
import React, { useCallback, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import HeaderTableComponent from '../../../../components/HeaderTableComponent'
import LeftArrow from '../../../../components/icons/LeftArrow'
import Debounce from '../../../../utils/Debounce'
import { ClearText } from '../../Style/style'
import AlreadySetPopUp from '../AlreadySetPopup'
import {
  Container,
  RoleButtonContainer,
  SearchByKeywords,
  SearchContainer,
  SetRoleDiv,
  StyledButton,
} from '../Dashboard/style'
import { formatWithHyphens } from '../RuleEngineCommonFunction'
import {
  IconButton,
  RulesNavigationTitle,
  StyledRuleHeader,
} from '../RulesCreationIndex/style'

const getCoursesLabel = rule => {
  const mapping = {
    P_G_DIPLOMA: 'PG Diploma',
    'P.G.DIPLOMA': 'PG Diploma',
    DIPLOMA: 'Diploma',
  }
  return mapping[rule] || rule
}

const RuleSetCourses = ({
  ruleId,
  criteriaCall,
  setSelectedStreamList,
  selectedStreamList,
  createRules,
  degreeStreamList,
  selectedSearchType,
  setSelectedSearchType,
  alreadySetDegreeList,
  setAlreadySetDegreeList,
  updateRules,
  getInstituteRulesById,
  isDegreeStreamLoading,
  ruleOverwrite,
  rulesDetails,
  criteriaTypes = [],
  loading,
}) => {
  const navigate = useNavigate()
  const ruleWay = useSelector(state => state?.rules?.ruleWay)

  const [isRuleExists, setIsRuleExists] = useState(false)
  const [ruleExistsList, setRuleExistsList] = useState([])
  const [selectedRole, setSelectedRole] = useState(criteriaTypes[0] || '')
  const [searchText, setSearchText] = useState('')
  const [resetSelection, setResetSelection] = useState(false)
  const [selectedDegreeStreamMap, setSelectedDegreeStreamMap] = useState([])

  const handleTableOpen = () => {
    rulesCreation()
  }

  const handleBackClick = () => {
    setSearchText('')
    if (ruleWay) {
      navigate('/ruleEngine/draft-page')
    } else {
      navigate('/ruleEngine')
    }
  }

  const handleRoleSelect = role => {
    setSelectedRole(role)
    criteriaCall({
      type: role === 'Diploma' ? 'DIPLOMA' : role,
      checkList: alreadySetDegreeList,
      search: searchText, // Keep the current search text instead of clearing it
      searchType: selectedSearchType === 'DEGREE' ? 'ALL' : 'STREAM',
    })
    // Don't clear search text - keep it so user can see results for same search term
  }

  const delayToFetchCriteria = useCallback(
    Debounce(data => {
      criteriaCall({
        type: selectedRole === 'Diploma' ? 'DIPLOMA' : selectedRole,
        search: data,
        searchType: selectedSearchType === 'DEGREE' ? 'ALL' : 'STREAM',
        checkList: alreadySetDegreeList,
      })
    }, 500),
    [selectedRole, selectedSearchType, alreadySetDegreeList]
  )

  useEffect(() => {
    console.log('Search type changed to:', selectedSearchType, 'with search text:', searchText)
    criteriaCall({
      type: selectedRole === 'Diploma' ? 'DIPLOMA' : selectedRole,
      search: searchText,
      searchType: selectedSearchType === 'DEGREE' ? 'ALL' : 'STREAM',
      checkList: alreadySetDegreeList,
    })
  }, [selectedSearchType])

  const handleSearchChange = event => {
    setSearchText(event.target.value)
    delayToFetchCriteria(event.target.value)
  }

  const handleClearAll = () => {
    setSearchText('')
    setResetSelection(true)
  }

  const rulesCreation = async () => {
    const degreeStreamMap = alreadySetDegreeList
      ?.filter?.(item => item.isSelected)
      ?.map(item => ({
        degreeId: item.degreeId,
        streamId: item.streamId,
      }))
    let payload = {
      degreeStreamMap: degreeStreamMap,
      ruleName: rulesDetails?.ruleName || '',
    }

    let response
    if (ruleId) {
      if (rulesDetails?.status === 0) {
        payload.effectiveDate = moment().format('YYYY-MM-DD')
      }
      payload.isExistingDegreeCheck = true
      response = await updateRules(payload, ruleId)
    } else {
      payload.effectiveDate = moment().format('YYYY-MM-DD')
      response = await createRules(payload)
    }

    if (response?.status === 200) {
      if (response?.data?.data?.isAlreadyCourseSet) {
        setIsRuleExists(true)
        setRuleExistsList(response?.data?.data?.courseList)
        setSelectedDegreeStreamMap(response?.data?.data?.degreeStreamMapId)
      } else {
        setIsRuleExists(false)
        navigate(`/ruleEngine/rule-creation/${response?.data?.data?.id}`)
      }
    }
  }

  useEffect(() => {
    if (criteriaTypes?.length > 0) {
      setSelectedRole(criteriaTypes[0])
    }
  }, [criteriaTypes])

  return (
    <>
      <Container>
        <StyledRuleHeader>
          <IconButton>
            <LeftArrow onClick={handleBackClick} />
          </IconButton>
          <RulesNavigationTitle>
            {'Setting new rules'}
            <span className="description">
              (This rule set will be effective from{' '}
              {loading
                ? ''
                : rulesDetails?.effectiveDate
                ? formatWithHyphens(rulesDetails?.effectiveDate)
                : formatWithHyphens(new Date())}
              )
            </span>
          </RulesNavigationTitle>
        </StyledRuleHeader>

        <RoleButtonContainer>
          {criteriaTypes.map(role => (
            <StyledButton
              key={role}
              selected={selectedRole === role}
              onClick={() => handleRoleSelect(role)}
              disabled={isDegreeStreamLoading}
            >
              {getCoursesLabel(role)}
            </StyledButton>
          ))}
        </RoleButtonContainer>

        <SearchContainer>
          <SetRoleDiv color="#000">Search using keywords</SetRoleDiv>
          <ClearText onClick={handleClearAll}>Clear all</ClearText>
        </SearchContainer>

        <div>
          <SearchByKeywords
            type="text"
            placeholder="Search for Degree / Department"
            value={searchText}
            onChange={handleSearchChange}
          />
        </div>

        <HeaderTableComponent
          handleApplyClick={handleTableOpen}
          resetSelection={resetSelection}
          setResetSelection={setResetSelection}
          setSelectedStreamList={setSelectedStreamList}
          selectedStreamList={selectedStreamList}
          degreeStreamList={degreeStreamList}
          setSelectedSearchType={setSelectedSearchType}
          alreadySetDegreeList={alreadySetDegreeList}
          setAlreadySetDegreeList={setAlreadySetDegreeList}
        />
      </Container>

      <AlreadySetPopUp
        isOpen={isRuleExists}
        setIsOpen={setIsRuleExists}
        data={ruleExistsList}
        getInstituteRulesById={getInstituteRulesById}
        ruleOverwrite={ruleOverwrite}
        ruleId={ruleId}
        selectedDegreeStreamMap={selectedDegreeStreamMap}
      />
    </>
  )
}

export default RuleSetCourses
